import type { AxiosInstance } from 'axios';

export type PaymentProvider = 'RAZORPAY' | 'COD' | 'WALLET';
export type PaymentStatus = string;

export interface Payment {
  id: string;
  orderGroupId: string;
  provider: PaymentProvider;
  method: string | null;
  status: PaymentStatus;
  amountPaise: number;
  currency: 'INR';
  providerOrderId: string | null;
  providerPaymentId: string | null;
  failureReason: string | null;
  authorizedAt: string | null;
  capturedAt: string | null;
  createdAt: string;
}

export interface CheckoutInit {
  paymentId: string;
  provider: PaymentProvider;
  keyId: string | null;
  providerOrderId: string | null;
  amountPaise: number;
  currency: 'INR';
  /** What the wallet covered; the gateway is charged `amountPaise`. */
  walletPaise: number;
  /** `ALREADY_PAID` is the wallet path: nothing left for the client to do. */
  nextAction: 'OPEN_RAZORPAY_CHECKOUT' | 'CONFIRM_COD' | 'ALREADY_PAID';
}

export function paymentEndpoints(http: AxiosInstance) {
  return {
    createCheckout: (body: {
      orderGroupId: string;
      provider: PaymentProvider;
      method?: string;
      /** Spend the wallet first; the server decides how much of it may apply. */
      useWallet?: boolean;
    }) => http.post<CheckoutInit>('/payments/checkout', body).then((r) => r.data),
    verify: (body: {
      razorpayOrderId: string;
      razorpayPaymentId: string;
      razorpaySignature: string;
    }) => http.post<Payment>('/payments/verify', body).then((r) => r.data),
    getPayment: (paymentId: string) =>
      http.get<Payment>(`/payments/${paymentId}`).then((r) => r.data),

    /**
     * Admin — refund money on a captured Razorpay payment. Defaults to the
     * whole remaining amount. The seller's settlement, the order and the stock
     * are not touched: returned or cancelled items go through those flows.
     */
    adminRefund: (body: { paymentId: string; amountPaise?: number; reason?: string }) =>
      http
        .post<{ id: string; amountPaise: number; status: string }>('/admin/payments/refunds', body)
        .then((r) => r.data),
  };
}

export type PaymentEndpoints = ReturnType<typeof paymentEndpoints>;
